import {
  type PropsWithChildren,
  createContext,
  useContext,
  useState,
} from 'react';

import { type InterviewBasicDto, type QuestionDto } from '@/services/api';

type InterviewQuestionContextValue = {
  candidateId: string;
  interviewId: string;
  securityToken: string;
  remainingQuestions: number;
  remainingTime: number;
  question: QuestionDto | undefined;
  answeredQuestions: number | undefined;
  canProceedInterviewStep: boolean;
  videoIsCollapsed: boolean;
  answerBeingSubmitted: boolean;
  setVideoIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setAnswerBeingSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  isCamRecordingEnabled: boolean;
  interview?: InterviewBasicDto;
};

const InterviewQuestionContext =
  createContext<InterviewQuestionContextValue | null>(null);

export const useInterviewQuestionContext = () => {
  const context = useContext(InterviewQuestionContext);
  if (!context) {
    throw new Error(
      'useInterviewQuestionContext must be used within a InterviewQuestionProvider'
    );
  }
  return context;
};

export const InterviewQuestionProvider: React.FC<
  PropsWithChildren<
    Omit<
      InterviewQuestionContextValue,
      'setVideoIsCollapsed' | 'videoIsCollapsed'
    >
  >
> = ({ children, ...value }) => {
  const [videoIsCollapsed, setVideoIsCollapsed] = useState(false);

  return (
    <InterviewQuestionContext.Provider
      value={{
        ...value,
        setVideoIsCollapsed,
        videoIsCollapsed,
      }}
    >
      {children}
    </InterviewQuestionContext.Provider>
  );
};
